import { QCONTENT_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import { QCONTENT_COLOR_STATUS, QCONTENT_STATUS_TEXT } from '@exam-management/components';
import {
  QuestionStatus,
  SearchQuestionsDocument,
  usePublishQuestionMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useQBankStructure, type QBankStructureLevel } from '@exam-management/qbs-provider';
import {
  type QContentDetailFormContextData,
  getPublishSingleQuestionSchema,
} from '@exam-management/qcontent-management';
import { useParams } from '@exam-management/router';
import {
  CircleIcon,
  ColorPalette,
  ContentCopyRoundedIcon,
  Divider,
  FileDownloadOutlinedIcon,
  Flex,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  MoreVertIcon,
  Text,
  Tooltip,
  styled,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useMemo, useState } from 'react';
import { useFormContext, type UseFormReturn } from 'react-hook-form';

import { useQContentDetailsModal } from '../../providers';
import { DisableSingleQuestionModal } from '../disable-single-question-modal';
import { PendingSingleQuestionModal } from '../pending-single-question-modal';
import { PublishSingleQuestionModal } from '../publish-single-question-modal';

const StyledMenuSection = styled(Flex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%',
  padding: theme.spacing(2),
}));

const validatePublishQuestion = (
  data: QContentDetailFormContextData,
  qBankStructureLevels: QBankStructureLevel[],
  formContext: UseFormReturn<QContentDetailFormContextData>,
) => {
  const PublishQuestionSchema = getPublishSingleQuestionSchema(qBankStructureLevels);
  const validationResult = PublishQuestionSchema.safeParse(data);

  if (!validationResult.success) {
    formContext.clearErrors();
    for (const zodError of validationResult.error.errors) {
      const field = zodError.path.join('.') as keyof QContentDetailFormContextData;
      formContext.setError(field, {
        type: 'manual',
        message: zodError.message,
      });
    }
    return false;
  } else {
    return true;
  }
};

export interface SingleQuestionHeaderMenuProps {
  status: QuestionStatus;
  handleSave: (data: QContentDetailFormContextData) => Promise<string | null | undefined>;
}

export default function SingleQuestionHeaderMenu(props: Readonly<SingleQuestionHeaderMenuProps>) {
  const { status, handleSave } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = anchorEl !== null;

  const { exportQuestion, duplicateQuestion } = useQContentDetailsModal();
  const formContext = useFormContext<QContentDetailFormContextData>();
  const [isPublishModalOpen, { open: openPublishModal, close: closePublishModal }] =
    useDisclosure();
  const [isPendingModalOpen, { open: openPendingModal, close: closePendingModal }] =
    useDisclosure();
  const [isDisableModalOpen, { open: openDisableModal, close: closeDisableModal }] =
    useDisclosure();

  const { contentId } = useParams('/qbank-structures/:structureId/qcontents/single/:contentId');

  const { showSnackbar } = useSnackbar();

  const { qBankStructureLevels } = useQBankStructure();

  const [publishQuestion] = usePublishQuestionMutation({
    onCompleted: () => {
      showSnackbar({
        message: 'Question has been published',
        variant: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to Publish Question',
        variant: 'error',
      });
    },
    refetchQueries: [SearchQuestionsDocument],
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleValidateBeforePublishQuestion = () => {
    const data = formContext.getValues();

    const isFormValid = validatePublishQuestion(data, qBankStructureLevels, formContext);

    if (isFormValid) {
      openPublishModal();
      handleClose();
    } else {
      showSnackbar({
        message: 'Unable to Publish Question',
        variant: 'error',
      });
    }
  };

  const handlePublishAction = async () => {
    await formContext.handleSubmit(async (data) => {
      await handleSave(data);

      await publishQuestion({
        variables: {
          id: contentId,
        },
      });
    })();

    closePublishModal();
  };

  const handleExportQuestionAction = () => {
    exportQuestion();
    handleClose();
  };

  const handleDuplicateQuestionAction = () => {
    duplicateQuestion();
    handleClose();
  };

  const handlePendingQuestionAction = () => {
    openPendingModal();
    handleClose();
  };

  const handleDisableQuestionAction = () => {
    openDisableModal();
    handleClose();
  };

  const { isSubmitting } = formContext.formState;

  const statusMenuOptions = useMemo(
    () => [
      {
        label: QCONTENT_STATUS_TEXT[QuestionStatus.Published],
        onClick: handleValidateBeforePublishQuestion,
        color: QCONTENT_COLOR_STATUS[QuestionStatus.Published],
        status: [QuestionStatus.Draft, QuestionStatus.Unpublished],
        disabled: isSubmitting,
        disabledTooltip: 'Saving in progress',
      },
      {
        label: QCONTENT_STATUS_TEXT[QuestionStatus.Unpublished],
        onClick: handlePendingQuestionAction,
        color: QCONTENT_COLOR_STATUS[QuestionStatus.Unpublished],
        status: [QuestionStatus.Published],
        disabled: isSubmitting,
        disabledTooltip: 'Saving in progress',
      },
      {
        label: QCONTENT_STATUS_TEXT[QuestionStatus.Disabled],
        color: QCONTENT_COLOR_STATUS[QuestionStatus.Disabled],
        onClick: handleDisableQuestionAction,
        status: [QuestionStatus.Unpublished],
        disabled: isSubmitting,
        disabledTooltip: 'Saving in progress',
      },
    ],
    [handleValidateBeforePublishQuestion, isSubmitting],
  );

  const actionMenuOptions = [
    {
      label: 'Duplicate to',
      IconComponent: ContentCopyRoundedIcon,
      onClick: handleDuplicateQuestionAction,
      requiredPermission: APP_PERMISSIONS.QBANK.QUESTION.CREATE,
      status: [
        QuestionStatus.Draft,
        QuestionStatus.Published,
        QuestionStatus.Unpublished,
        QuestionStatus.Disabled,
      ],
    },
    {
      label: 'Export Question',
      IconComponent: FileDownloadOutlinedIcon,
      onClick: handleExportQuestionAction,
      // TODO: Any permission to export question ?
      requiredPermission: [APP_PERMISSIONS.QBANK.QUESTION.VIEW],
      status: [
        QuestionStatus.Draft,
        QuestionStatus.Published,
        QuestionStatus.Unpublished,
        QuestionStatus.Disabled,
      ],
    },
  ];

  const filteredStatusMenuOptions = useMemo(
    () => statusMenuOptions.filter((option) => option.status.includes(status)),
    [statusMenuOptions, status],
  );

  const filteredActionMenuOptions = useMemo(
    () => actionMenuOptions.filter((option) => option.status.includes(status)),
    [actionMenuOptions, status],
  );

  const shouldDisplayActionMenu = filteredActionMenuOptions.length > 0;
  const shouldDisplayStatusMenu = filteredStatusMenuOptions.length > 0;

  if (!shouldDisplayActionMenu && !shouldDisplayStatusMenu) {
    return null;
  }

  return (
    <>
      <Divider orientation='vertical' flexItem sx={{ width: 'unset' }} />
      <IconButton
        data-testid={QCONTENT_DETAILS_DATA_TEST_ID.HEADER_MENU}
        sx={(theme) => ({
          borderRadius: theme.shape.rounded(1),
          border: `1px solid ${theme.palette.grey[400]}`,
        })}
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleClose}
        MenuListProps={{
          sx: (theme) => ({
            borderRadius: theme.shape.rounded(2),
            width: '180px',
            p: theme.spacing(0),
          }),
        }}
      >
        {shouldDisplayStatusMenu ? (
          <WithPermission requiredPermission={APP_PERMISSIONS.QBANK.QUESTION.STATUS}>
            <StyledMenuSection>
              <Text
                variant='caption'
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightMedium,
                  px: theme.spacing(2),
                })}
              >
                Update Status to
              </Text>
              {filteredStatusMenuOptions.map(
                ({ label, onClick, color, disabled, disabledTooltip }) => (
                  <Tooltip
                    key={`single-question-menu-item-${label}`}
                    title={disabled ? disabledTooltip : ''}
                    placement='left'
                  >
                    <span>
                      <MenuItem onClick={onClick} disabled={disabled} sx={{ width: '100%' }}>
                        <CircleIcon
                          sx={(theme) => ({
                            color: Object.values(ColorPalette).includes(color as ColorPalette)
                              ? theme.palette[color as ColorPalette].main
                              : color,
                            width: '10px',
                            height: '10px',
                            mr: theme.spacing(2),
                          })}
                        />
                        <ListItemText>{label}</ListItemText>
                      </MenuItem>
                    </span>
                  </Tooltip>
                ),
              )}
            </StyledMenuSection>
          </WithPermission>
        ) : null}
        {shouldDisplayActionMenu && shouldDisplayStatusMenu ? <Divider /> : null}
        {shouldDisplayActionMenu ? (
          <>
            <StyledMenuSection>
              <Text
                variant='caption'
                sx={(theme) => ({
                  fontWeight: theme.typography.fontWeightMedium,
                  px: theme.spacing(2),
                })}
              >
                Actions
              </Text>
              {filteredActionMenuOptions.map(
                ({ label, IconComponent, requiredPermission, onClick }) => (
                  <WithPermission
                    key={`single-question-header-menu-${label}`}
                    requiredPermission={requiredPermission}
                  >
                    <MenuItem onClick={onClick}>
                      <IconComponent
                        sx={{
                          width: '24px',
                          height: '24px',
                          mr: (theme) => theme.spacing(2),
                          color: (theme) => theme.palette.grey[600],
                        }}
                      />
                      <ListItemText>{label}</ListItemText>
                    </MenuItem>
                  </WithPermission>
                ),
              )}
            </StyledMenuSection>
          </>
        ) : null}
      </Menu>
      <PublishSingleQuestionModal
        open={isPublishModalOpen}
        onClose={closePublishModal}
        onAccept={handlePublishAction}
      />
      <PendingSingleQuestionModal open={isPendingModalOpen} onClose={closePendingModal} />
      <DisableSingleQuestionModal open={isDisableModalOpen} onClose={closeDisableModal} />
    </>
  );
}
